import Modal from 'react-modal';
import { BiX } from 'react-icons/bi';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import Button from '../../../components/UI/Button';
import iconPin from '../../../images/etc/icon_pin.svg';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import axios from 'axios';
import DraggableCard from './Components/DraggableCard';
import { getFormatDate } from '../../../function/common';
import { update } from 'lodash';

interface IShopModalProps {
    isOpen: boolean;
    onClose: () => void;
    refreshData: () => void;
}

export interface IShopList {
    /**
     * @alias DB번호 (pk)
     */
    id: number;
    /**
     * @alias 노출 순서
     */
    sorting: number;
    /**
     * @alias 쇼핑몰 명
     */
    shopName: string;
    /**
     * @alias 카테고리
     */
    cate: string;
    /**
     * @alias 상단 노출 상태
     * @description 0: NONE 1:연동예정 2:신규
     */
    priorityStatus: number;
    /**
     * @alias 상단 노출 시작 날짜
     */
    exposureStartDate: string | null;
    /**
     * @alias 상단 노출 종료 날짜
     */
    exposureEndDate: string | null;
}

export interface ISortSendData {
    /**
     * @alias 변경할 쇼핑몰 DB번호 (pk)
     */
    id: number;
    /**
     *  @alias 변경할 쇼핑몰 노출 순서
     */
    targetRank?: number;
    /**
     * @alias 변경할 쇼핑몰 노출 시작 날짜
     */
    exposureStartDate?: string | null;
    /**
     * @alias 변경할 쇼핑몰 노출 종료 날짜
     */
    exposureEndDate?: string | null;
    /**
     * @alias 변경할 쇼핑몰 상단 노출 상태
     */
    priorityStatus?: number;
}

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 10px 0 25px;
    height: 55px;
    border-bottom: 1px solid #dfdfdf;
`;

const ModalTitle = styled.h2`
    font-size: 16px;
    color: #333;
`;

const InstructionsList = styled.ul`
    margin-bottom: 20px;
    font-size: 13px;
    color: #777;
    line-height: 1.5;
`;

const ModalBody = styled.div`
    padding: 20px 30px;
    height: calc(100% - 60px - 55px);
    overflow-y: auto;
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 0 20px;
    height: 60px;
    background: #fff;
    border-top: 1px solid #dfdfdf;
    position: relative;
    z-index: 2;
`;

const BoardSection = styled.div`
    margin-bottom: 10px;
`;

const BoardHeader = styled.div<{ isGeneral: boolean }>`
    padding: 0 24px;
    height: 36px;
    line-height: 36px;
    font-size: 14px;
    color: #333;
    font-weight: 700;
    background: ${(props) => (props.isGeneral ? '#F5F5F5' : '#EDF4FA')};
    border-radius: 4px;
`;

const EmptyStateContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 120px;
    font-size: 14px;
    font-weight: 500;
`;

const BoardBody = styled.div<{ isDraggingOver: boolean; isGeneral: boolean }>`
    border-radius: '4px';
    margin-top: ${(props) => (props.isDraggingOver ? '0' : '-1px')};
    background: ${(props) => (props.isDraggingOver ? '#EDF7FF' : '#FFFFFF')};
    border: ${(props) => props.isDraggingOver && '1px solid #0072CE'};
    overflow: ${(props) => (props.isGeneral ? 'auto' : 'hidden')};
    max-height: ${(props) => (props.isGeneral ? '400px' : 'auto')};
`;

const ShopModal: React.FC<IShopModalProps> = ({ isOpen, onClose, refreshData }) => {
    // 작업 데이터
    const [shopList, setShopList] = useState<IShopList[]>([]);
    /** 상단 고정 상태 세부 분류  */
    // 연동예정
    const [supportExpected, setSupportExpected] = useState<IShopList[]>([]);
    // 신규
    const [newIntegration, setNewIntegration] = useState<IShopList[]>([]);
    // 일반(NONE)
    const [general, setGeneral] = useState<IShopList[]>([]);
    // 상단 고정 상태 관리
    const [shopStatus, setShopStatus] = useState([supportExpected, newIntegration, general]);
    // 노출 순서 변경을 위한 data 리스트 관리
    const [updateData, setUpdateData] = useState<ISortSendData[]>([]);

    // 모달 커스텀
    const customStyles = {
        overlay: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
        content: {
            width: '800px',
            height: '95vh',
            padding: '0',
            position: 'relative' as 'relative',
            border: '1px solid #DFDFDF',
            borderRadius: '10px',
            background: '#FFFFFF 0% 0% no-repeat padding-box',
            overflow: 'hidden',
            inset: 'auto',
        },
    };

    /**
     * @alias shopList 업데이트 핸들러
     * @description shopList는 모든 데이터를 가지고 있고, updateData는 변경된 데이터만 가지고 있음
     */
    const setShopListHandler = (id: number, shop: IShopList[]) => {
        setShopList(shop);
        if (id < 0) return;
        // updateData에 존재하지 않으면 날짜가 변경된 데이터이므로 updateData에 추가
        const existingItem = updateData.find((item) => item.id === id);
        if (!existingItem) {
            const target = shop.find((item) => item.id === id);

            if (target) {
                const { id, priorityStatus, exposureStartDate, exposureEndDate } = target;
                updateUpdateData({ id, priorityStatus, exposureStartDate, exposureEndDate });
            }
        }
    };

    /**
     * @alias 드래그 앤 드롭 이벤트 핸들러
     */
    function onDragEnd({ destination, source, draggableId }: DropResult) {
        if (!destination) {
            return;
        }
        const id = parseInt(draggableId);
        const sIndex = +source.droppableId;
        const dIndex = +destination.droppableId;

        let newState;
        /** 모달 내에서 이동처리 시작 **/
        //같은 리스트 내에서 이동
        if (destination?.droppableId === source.droppableId) {
            const boardCopy = shopStatus[sIndex];
            const taskObj = boardCopy[source.index];
            boardCopy.splice(source.index, 1);
            boardCopy.splice(destination.index, 0, taskObj);
            newState = [...shopStatus];
            newState[sIndex] = boardCopy;
        }
        //다른 리스트로 이동
        else {
            if (dIndex === 0 && supportExpected.length >= 3) {
                Swal.fire({
                    text: '연동 예정 리스트는 최대 3개까지 담을 수 있습니다.',
                    icon: 'warning',
                    confirmButtonText: '확인',
                });
                return;
            }

            if (dIndex === 1 && newIntegration.length >= 3) {
                Swal.fire({
                    text: '신규 리스트는 최대 3개까지 담을 수 있습니다.',
                    icon: 'warning',
                    confirmButtonText: '확인',
                });
                return;
            }
            const sourceBoard = shopStatus[sIndex];
            const tmpObj = sourceBoard[source.index];
            const destinationBoard = [...shopStatus[dIndex]];
            sourceBoard.splice(source.index, 1);
            destinationBoard.splice(destination?.index, 0, tmpObj);
            newState = [...shopStatus];
            newState[sIndex] = sourceBoard;
            newState[dIndex] = destinationBoard;
        }

        const movedItem = newState[dIndex].find((item) => item.id === id);
        if (movedItem) {
            // 상단 고정 상태를 연동예정 or 신규로 가는 경우
            if (dIndex === 0 || dIndex === 1) {
                // 만약 이동하는 아이템이 고정 기간이 없다면 설정해줌(기본값)
                if (!movedItem.exposureStartDate) {
                    movedItem.exposureStartDate = new Date().toISOString();
                }
                if (!movedItem.exposureEndDate) {
                    const oneMonthLater = new Date();
                    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
                    movedItem.exposureEndDate = oneMonthLater.toISOString();
                }
                // 상단 고정 상태 설정해줌
                movedItem.priorityStatus = dIndex === 0 ? 1 : 2;
            }
            // 일반으로 가는 경우는 상단 고정 상태를 0으로 설정하고 고정 기간 null 처리
            else {
                movedItem.priorityStatus = 0;
                movedItem.exposureEndDate = null;
                movedItem.exposureStartDate = null;
            }
        } else {
            Swal.fire({
                title: '실패',
                html: `이동할 데이터가 없습니다.`,
                icon: 'error',
            });
            return;
        }

        // shopStatus를 기준으로 백엔드로 전송할 targetId(pk)랑 targetRank 계산
        const updatedShopList = newState.flat().map((shop, index) => ({
            ...shop,
            sorting: index + 1,
        }));
        let targetRank;
        const draggableIndex = updatedShopList.findIndex((item) => item.id === id);
        // 서로 다른 상단 고정으로 이동
        if (source.droppableId !== destination.droppableId) {
            // 높은 랭크에서 낮은 랭크로 이동 (2 -> 5) index + 2
            // 낮은 랭크에서 높은 랭크로 이동 (5 -> 2) index + 1
            targetRank = source.droppableId < destination.droppableId ? draggableIndex + 2 : draggableIndex + 1;
        } else {
            // 같은 상단 고정 내에서 이동
            // 높은 랭크에서 낮은 랭크로 이동 (2 -> 5) index + 2
            // 낮은 랭크에서 높은 랭크로 이동 (5 -> 2) index + 1
            targetRank = source.index < destination.index ? draggableIndex + 2 : draggableIndex + 1;
        }
        // 상태 업데이트
        // 여기서 updateDate는 추가 안하므로 -1
        setShopListHandler(-1, updatedShopList);

        // 이동한 아이템의 index와 targetRank 계산
        const exposureStartDate = movedItem?.exposureStartDate;
        const exposureEndDate = movedItem?.exposureEndDate;
        const priorityStatus = movedItem?.priorityStatus;

        //updateData에 필요한 데이터들만 추출해서 업데이트
        updateUpdateData({
            id,
            targetRank,
            priorityStatus,
            exposureStartDate,
            exposureEndDate,
        });
    }

    /**
     * @alias updateData 업데이트하는 함수
     */
    const updateUpdateData = ({
        id,
        targetRank,
        priorityStatus,
        exposureStartDate,
        exposureEndDate,
    }: ISortSendData) => {
        setUpdateData((prevData) => [
            ...prevData,
            {
                id,
                targetRank,
                priorityStatus,
                exposureStartDate,
                exposureEndDate,
            },
        ]);
    };

    // shopList가 업데이트되면 targetId를 기준으로 shoplist에서 해당 데이터를 찾아서 업데이트
    useEffect(() => {
        updateData.map((item) => {
            const target = shopList.find((shop) => shop.id === item.id);
            if (target) {
                item.exposureStartDate = target.exposureStartDate;
                item.exposureEndDate = target.exposureEndDate;
                item.priorityStatus = target.priorityStatus;
            }
        });
    }, [shopList]);

    //1. 초기 데이터 조회 및 설정
    useEffect(() => {
        const getJobData = async () => {
            Swal.fire({
                title: '잠시만 기다려 주세요.',
                html: '작업 조회중 ...',
                allowOutsideClick: false,
                showConfirmButton: false,
                willOpen: () => {
                    Swal.showLoading();
                },
            });
            try {
                const { data: result } = await axios.get(
                    `${process.env.REACT_APP_BACKEND_ENDPOINT}/restApi/plto/get-shop-list/rank-chagne`,
                );

                if (result.totalCount === 0) {
                    Swal.fire({
                        title: '실패',
                        html: `정보가 조회되지 않습니다.`,
                        icon: 'error',
                    });
                } else {
                    // 작업 데이터 설정
                    setShopListHandler(-1, result.result);
                    Swal.close();
                }
            } catch (error: any) {
                Swal.fire({
                    title: '실패',
                    html: `${error.message}`,
                    icon: 'error',
                });
            }
        };
        getJobData();
    }, []);

    // 2. 상단 고정 상태 세부 분류 - 이거를 해야 모달창에서 분리됨
    // 지원 예정, 신규, 일반
    useEffect(() => {
        const supportExpected = shopList.filter((item) => item.priorityStatus === 1);
        const newIntegration = shopList.filter((item) => item.priorityStatus === 2);
        const general = shopList.filter((item) => item.priorityStatus === 0);
        setNewIntegration(newIntegration);
        setSupportExpected(supportExpected);
        setGeneral(general);
        setShopStatus([supportExpected, newIntegration, general]);
    }, [shopList]);

    /**
     * @alias 날짜 변경 핸들러
     */
    const handleDateChange = (id: number, date: Date | null, type: 'start' | 'end') => {
        if (!date) {
            return;
        }
        const inputDate = getFormatDate(date as Date);

        // 기존 날짜와 변경하려는 날짜의 값이 같을 경우 종료
        if (!validateDate(inputDate, type, id)) {
            return;
        }

        const inputKey = type === 'start' ? 'exposureStartDate' : 'exposureEndDate';

        setShopListHandler(
            id,
            shopList.map((item) =>
                item.id === id
                    ? {
                          // 선택한 date를 저장, 없으면 null
                          ...item,
                          [inputKey]: inputDate,
                      }
                    : item,
            ),
        );
    };

    /**
     * @alias 노출 순서 쇼핑몰 시작, 종료 날짜 유효성 검사
     * @description 기존 날짜와 변경하려는 날짜의 값이 같을 경우 종료
     */
    const validateDate = (inputValue: string, type: 'start' | 'end', id: number): boolean => {
        const shopItem = shopList.find((item) => item.id === id);
        if (shopItem?.exposureStartDate === inputValue && type === 'start') {
            return false;
        }
        if (shopItem?.exposureEndDate === inputValue && type === 'end') {
            return false;
        }

        return true;
    };

    /**
     * @alias 키보드 입력 시 날짜 유효성 검사
     */
    const handleDateBlur = (id: number, inputValue: string, type: 'start' | 'end') => {
        // 기존 날짜와 변경하려는 날짜의 값이 같을 경우 종료
        if (!validateDate(inputValue, type, id)) {
            return;
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 입력된 날짜를 '-'를 기준으로 분리
        let [year, month, day] = inputValue.split('-').map((val) => parseInt(val, 10));
        // 년, 월, 일이 숫자로 잘 파싱되었는지 확인
        if (isNaN(year) || isNaN(month) || isNaN(day) || year.toString().length > 4) {
            Swal.fire({
                text: 'yyyy-mm-dd 형식으로 입력해주세요.',
                icon: 'warning',
                confirmButtonText: '확인',
            });

            return;
        }
        // 1. 월(Month) 유효성 검사
        if (month < 1) month = 1;
        else if (month > 12) month = 12;
        // 2. 일(Date) 유효성 검사
        const lastDayOfMonth = new Date(year, month, 0).getDate(); // 해당 월의 마지막 날짜
        if (day < 1) day = 1;
        else if (day > lastDayOfMonth) day = lastDayOfMonth;
        // 최종 유효성을 반영한 날짜 생성
        let validatedDate = new Date(year, month - 1, day);

        // 3. 오늘 이전 날짜일 경우 오늘로 설정
        if (validatedDate < today) {
            validatedDate = today;
        }
        // 4. handleDateChange 호출하여 변경 사항 반영
        handleDateChange(id, validatedDate, type);
    };

    /**
     * @alias 노출 순서 변경 요청
     */
    async function updateShopRank() {
        try {
            if (updateData.length === 0) {
                Swal.fire({
                    title: '실패',
                    html: `변경된 내용이 없습니다.`,
                    icon: 'error',
                });
                return;
            }
            const { data: result } = await axios.post(
                `${process.env.REACT_APP_BACKEND_ENDPOINT}/restApi/plto/update-shop-rank`,
                {
                    updateData,
                },
            );
            if (result.success) {
                Swal.fire({
                    title: '성공',
                    html: `노출 순서가 변경되었습니다.`,
                    icon: 'success',
                });
                refreshData();
            } else {
                Swal.fire({
                    title: '실패',
                    html: `노출 순서 변경에 실패했습니다.`,
                    icon: 'error',
                });
                return;
            }
        } catch (e: any) {
            Swal.fire({
                title: '실패',
                html: `${e.message}`,
                icon: 'error',
            });
            return;
        } finally {
            onClose();
        }
    }

    return (
        <Modal ariaHideApp={false} isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
            <ModalHeader>
                <ModalTitle> 노출 순서 설정</ModalTitle>
                <BiX color="#D4D4D4" size="2rem" onClick={onClose} style={{ cursor: 'pointer' }} />
            </ModalHeader>
            <ModalBody>
                <InstructionsList>
                    <li>- 아래 화면에서 보이는 순서 그대로 연동 쇼핑몰 화면에 노출됩니다.</li>
                    <li>
                        - 지원 예정과 신규 연동은 각각 최대 3개까지 지정 가능하며, 지원예정 &gt; 신규 연동 &gt; 일반
                        순서로 노출됩니다.
                    </li>
                </InstructionsList>
                <DragDropContext onDragEnd={onDragEnd}>
                    {shopStatus.map((status, info) => (
                        <Droppable key={info} droppableId={`${info}`}>
                            {(provided, snapshot) => (
                                <BoardSection>
                                    <BoardHeader isGeneral={info === 2}>
                                        {info === 0 && (
                                            <>
                                                <img
                                                    src={iconPin}
                                                    style={{ marginRight: '5px', verticalAlign: 'middle' }}
                                                />
                                                지원 예정(상단고정)
                                            </>
                                        )}
                                        {info === 1 && (
                                            <>
                                                <img
                                                    src={iconPin}
                                                    style={{ marginRight: '5px', verticalAlign: 'middle' }}
                                                />
                                                신규 연동(상단고정)
                                            </>
                                        )}
                                        {info === 2 && '일반'}
                                    </BoardHeader>
                                    <BoardBody
                                        ref={provided.innerRef}
                                        isDraggingOver={snapshot.isDraggingOver}
                                        isGeneral={info === 2} // 일반 부분일 때만 스크롤 적용
                                        {...provided.droppableProps}
                                    >
                                        {status.length === 0 && (
                                            <EmptyStateContainer>
                                                {info === 0 && '지원 예정'}
                                                {info === 1 && '신규 연동'}
                                                으로 상단 고정된 쇼핑몰이 없습니다.
                                            </EmptyStateContainer>
                                        )}
                                        {status.map((shop, index) => (
                                            <DraggableCard
                                                key={shop.id}
                                                index={index}
                                                shop={shop}
                                                info={info}
                                                handleDateChange={handleDateChange}
                                                handleDateBlur={handleDateBlur}
                                            />
                                        ))}
                                        {provided.placeholder}
                                    </BoardBody>
                                </BoardSection>
                            )}
                        </Droppable>
                    ))}
                </DragDropContext>
            </ModalBody>

            <ModalFooter>
                <Button onClick={onClose} variant="outlined">
                    취소
                </Button>
                &nbsp;&nbsp;
                <Button
                    onClick={() => {
                        updateShopRank();
                    }}
                >
                    수정
                </Button>
            </ModalFooter>
        </Modal>
    );
};
export default ShopModal;
